/**
 * MatterContextStep — Step 4 of the drafting wizard.
 *
 * Rendered only when a matter is selected in Step 3. Mirrors the toggle
 * grid in DocumentCustomizationPanel — toggles are gated by per-matter
 * data availability (fetched from /matters/<uuid>/context-availability).
 */

import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  PiTextAlignLeftLight, PiScalesLight, PiGavelLight, PiFileLight,
  PiQuestionLight, PiNoteLight, PiListChecksLight, PiUserLight,
  PiClockCounterClockwiseLight, PiFileTextLight, PiInfoLight,
} from 'react-icons/pi';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import apis from '../../../utils/apis';
import { hasCasesSupport } from '../../Matters/matterConstants';
import Toggle from '../../shared/TailwindComponents/Toggle';
import { Well } from '../../shared/brand/Field';
import { SELECTED_WELL } from '../../shared/brand/selectedCard';
import type { MatterContextOptions } from '../../../store/slices/draftingSlice';
import Spinner from '../../shared/TailwindComponents/Spinner';

interface SectionConfig {
  key: keyof MatterContextOptions;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const SECTIONS: SectionConfig[] = [
  { key: 'description',         label: 'Matter description',  description: 'AI-generated matter summary and case description',  icon: <PiTextAlignLeftLight className="w-5 h-5" /> },
  { key: 'laws',                label: 'Laws & regulations',  description: 'Relevant statutes and legal provisions from analysis', icon: <PiScalesLight className="w-5 h-5" /> },
  { key: 'cases',               label: 'Case precedents',     description: 'Relevant court decisions and legal precedents',        icon: <PiGavelLight className="w-5 h-5" /> },
  { key: 'source_documents',    label: 'Source documents',    description: 'Uploaded case files and documents',                    icon: <PiFileLight className="w-5 h-5" /> },
  { key: 'questions',           label: 'Questionnaire answers', description: 'Finalized answers from the matter questionnaire',    icon: <PiQuestionLight className="w-5 h-5" /> },
  { key: 'notes',               label: 'Matter notes',        description: 'Notes and observations added to the matter',           icon: <PiNoteLight className="w-5 h-5" /> },
  { key: 'tasks',               label: 'Matter tasks',        description: 'Suggested and active tasks related to the matter',      icon: <PiListChecksLight className="w-5 h-5" /> },
  { key: 'client_info',         label: 'Client information',  description: 'Client details, roles, and contact information',        icon: <PiUserLight className="w-5 h-5" /> },
  { key: 'reference_documents', label: 'Reference attachments', description: 'Files uploaded in Step 6 of this wizard',             icon: <PiFileTextLight className="w-5 h-5" /> },
  { key: 'previous_version',    label: 'Previous version',    description: 'Content from the last generated version of this document', icon: <PiClockCounterClockwiseLight className="w-5 h-5" /> },
];

interface MatterContextStepProps {
  matterUuid: string;
  matterJurisdiction?: string | null;
  /** True when at least one attachment is queued for upload in Step 6. */
  hasAttachments: boolean;
  value: MatterContextOptions;
  onChange: (next: MatterContextOptions) => void;
}

export const DEFAULT_MATTER_CONTEXT_OPTIONS: Required<MatterContextOptions> = {
  description: true, laws: true, cases: true, source_documents: true,
  questions: true, notes: true, tasks: true, client_info: true,
  previous_version: true, reference_documents: true,
};

const MatterContextStep: React.FC<MatterContextStepProps> = ({
  matterUuid, matterJurisdiction, hasAttachments, value, onChange,
}) => {
  const { t } = useTranslation();
  const [availability, setAvailability] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await apis.get(`/matters/${matterUuid}/context-availability`);
        if (alive && res?.data?.availability) {
          setAvailability(res.data.availability);
        }
      } catch {
        if (alive) setAvailability({});
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [matterUuid]);

  const set = (key: keyof MatterContextOptions, v: boolean) => {
    onChange({ ...value, [key]: v });
  };

  const showCases = hasCasesSupport(matterJurisdiction ?? undefined);

  return (
    <div className="space-y-3">
      <Well className="flex items-start gap-2 border-info-200 bg-info-50/60 text-xs text-info-700 dark:border-info-800/50 dark:bg-info-900/20 dark:text-info-300">
        <PiInfoLight className="mt-0.5 h-4 w-4 shrink-0" />
        <p>{t('Choose which slices of matter context PONS should include when generating this draft. Disabled rows have no data on this matter.')}</p>
      </Well>

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-fg-muted">
          <Spinner size="glyph" className="!h-4 !w-4" />
          {t('Loading available context…')}
        </div>
      ) : (
        <div className="space-y-2">
          {SECTIONS.filter((s) => s.key !== 'cases' || showCases).map((section) => {
            const enabled = value[section.key] ?? true;
            let isAvailable: boolean;
            if (section.key === 'reference_documents') {
              isAvailable = hasAttachments;
            } else if (section.key === 'previous_version') {
              isAvailable = false; // standalone draft always has no prior version on first gen
            } else {
              isAvailable = availability[section.key] !== undefined ? availability[section.key] : true;
            }
            const isDisabled = !isAvailable;
            const card = (
              <Well
                key={section.key}
                className={clsx(
                  'flex items-center justify-between transition-colors',
                  isDisabled && 'opacity-40',
                  !isDisabled && enabled && SELECTED_WELL,
                  !isDisabled && !enabled && 'opacity-60',
                )}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={!isDisabled && enabled ? 'text-fg-body' : 'text-fg-muted'}>
                    {section.icon}
                  </div>
                  <div className="min-w-0">
                    <p className={clsx('text-sm font-medium', !isDisabled && enabled ? 'text-fg-strong' : 'text-fg-muted')}>
                      {t(section.label)}
                      {isDisabled && (
                        <span className="ml-2 text-[10px] font-normal text-fg-muted">{t('No data')}</span>
                      )}
                    </p>
                    <p className="text-xs text-fg-muted truncate">{t(section.description)}</p>
                  </div>
                </div>
                <Toggle
                  enabled={!isDisabled && enabled}
                  onToggle={(v) => { if (!isDisabled) set(section.key, v); }}
                />
              </Well>
            );
            return isDisabled ? (
              <Tooltip key={section.key} title={t('No data available for this matter.')} arrow placement="top">
                <div>{card}</div>
              </Tooltip>
            ) : <React.Fragment key={section.key}>{card}</React.Fragment>;
          })}
        </div>
      )}
    </div>
  );
};

export default MatterContextStep;
